import api from '@/lib/api';
import type { Vendor } from '@pms/shared';

// ─── Query / payload types ────────────────────────────────────────────────────

export interface ListVendorsQuery {
  propertyId: string;
  activeOnly?: boolean | undefined;
}

export interface CreateVendorPayload {
  propertyId: string;
  name: string;
  contactName?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  terms?: string | undefined;
  isActive?: boolean | undefined;
}

export interface UpdateVendorPayload {
  name?: string | undefined;
  contactName?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  terms?: string | undefined;
  isActive?: boolean | undefined;
}

export interface ReceiveStockPayload {
  propertyId: string;
  productId: string;
  vendorId: string;
  /** Whole units received, >= 1. */
  qty: number;
  /** Per-unit cost. */
  unitCost: number;
  reference?: string | undefined;
  /** ISO date (yyyy-MM-dd). */
  date?: string | undefined;
}

export interface ReceiveStockResponse {
  productId: string;
  stockOnHand: number;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchVendors(query: ListVendorsQuery): Promise<Vendor[]> {
  const { data } = await api.get<Vendor[]>('/retail/vendors', { params: query });
  return data;
}

export async function createVendor(payload: CreateVendorPayload): Promise<Vendor> {
  const { data } = await api.post<Vendor>('/retail/vendors', payload);
  return data;
}

export async function updateVendor(
  id: string,
  payload: UpdateVendorPayload,
): Promise<Vendor> {
  const { data } = await api.patch<Vendor>(`/retail/vendors/${id}`, payload);
  return data;
}

export async function deleteVendor(id: string): Promise<void> {
  await api.delete(`/retail/vendors/${id}`);
}

/** Receive stock against a vendor — increments on-hand via the adjustment ledger. */
export async function receiveStock(
  payload: ReceiveStockPayload,
): Promise<ReceiveStockResponse> {
  const { productId, ...body } = payload;
  const { data } = await api.post<ReceiveStockResponse>(
    `/retail/stock/${productId}/receive`,
    body,
  );
  return data;
}
